import { useState, useEffect, useRef, useCallback } from 'react';
import { UseJudgeTimerReturn, TimerState } from '../types/judge';

/**
 * Custom hook for managing judge station timer functionality
 * Handles routine timing with precise countdown and WebSocket synchronization
 */
export const useJudgeTimer = (): UseJudgeTimerReturn => {
  const [timer, setTimer] = useState<TimerState>({
    isRunning: false,
    seconds: 0,
    totalSeconds: 0,
    startTime: null,
    endTime: null
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Start timer with duration and optional start/end times
  const startTimer = useCallback((durationSeconds: number, startTime?: number, endTime?: number) => {
    const now = Date.now();
    const calculatedStartTime = startTime || now;
    const calculatedEndTime = endTime || (now + (durationSeconds * 1000));

    setTimer(prev => ({
      ...prev,
      isRunning: true,
      seconds: durationSeconds,
      totalSeconds: durationSeconds,
      startTime: calculatedStartTime,
      endTime: calculatedEndTime
    }));
  }, []);

  // Stop timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setTimer(prev => ({
      ...prev,
      isRunning: false,
      startTime: null,
      endTime: null
    }));
  }, []);

  // Reset timer with new duration
  const resetTimer = useCallback((durationSeconds: number) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setTimer({
      isRunning: false,
      seconds: durationSeconds,
      totalSeconds: durationSeconds,
      startTime: null,
      endTime: null
    });
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (timer.isRunning && timer.endTime) {
      timerRef.current = setInterval(() => {
        const currentTime = Date.now();
        const remainingMs = Math.max(0, timer.endTime! - currentTime);
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        
        setTimer(prev => ({
          ...prev,
          seconds: remainingSeconds
        }));
        
        if (remainingMs <= 0) {
          // Timer complete
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setTimer(prev => ({
            ...prev,
            isRunning: false,
            startTime: null,
            endTime: null
          }));
        }
      }, 100); // Update more frequently for smoother countdown
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timer.isRunning, timer.endTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return {
    timer,
    startTimer,
    stopTimer,
    resetTimer
  };
};








